import React from 'react';
import { Utils } from 'core/utils';
import DeleteDialog from 'core/components/shared/DeleteDialog';
import { useQuery, useQueryClient } from 'react-query';
import CustomModal from 'cms/core/shared/dialogs/CustomModal';
import ScheduleForm from './forms/ScheduleForm';
import { JobHttpService } from '../service';
import UseScheduleList from '../hooks/UseScheduleList';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    IconButton,
    Stack,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment
} from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';

function ScheduleList() {
    const queryClient = useQueryClient();
    const { data, isLoading, isError, removeItemFromList, refetch } = UseScheduleList();
    const [deleteDialogIsOpen, setDeleteDialogIsOpen] = React.useState(false);
    const [addDialogIsOpen, setAddDialogIsOpen] = React.useState(false);
    const [selectedId, setSelectedId] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState('');

    const schedules = data?.results || [];

    const filteredSchedules = schedules.filter(schedule =>
        schedule.title?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const _handleDelete = (e, id) => {
        e.stopPropagation();
        setSelectedId(id);
        setDeleteDialogIsOpen(true);
    };

    const _onDeleteDone = () => {
        setDeleteDialogIsOpen(false);
        queryClient.invalidateQueries('schedules_list');
        setSelectedId(null);
    };

    const _handleEdit = (e, id) => {
        e.stopPropagation();
        setSelectedId(id);
        setAddDialogIsOpen(true);
    };

    const _handleAdd = () => {
        setSelectedId(null);
        setAddDialogIsOpen(true);
    };

    const _handleFormDone = () => {
        refetch();
        queryClient.invalidateQueries('schedules_list');
        setAddDialogIsOpen(false);
        setSelectedId(null);
    };

    return (
        <Box>
            <Paper 
                elevation={0}
                sx={{ 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2
                }}
            >
                {/* Header with Search and Add Button */}
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Typography variant="h6" fontWeight={600} color="text.primary">
                            Schedules
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField
                                size="small"
                                placeholder="Search schedules..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ width: 250 }}
                            />
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={_handleAdd}
                            >
                                Add Schedule
                            </Button>
                        </Stack>
                    </Stack>
                </Box>

                {/* Table */}
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress />
                    </Box>
                ) : isError ? (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="error">Failed to load schedules. Please try again.</Alert>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>ID</strong></TableCell>
                                    <TableCell><strong>Title</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell><strong>Created at</strong></TableCell>
                                    <TableCell align="right"><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredSchedules.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {searchTerm ? 'No schedules found matching your search.' : 'No schedules found.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredSchedules.map((item) => (
                                        <TableRow 
                                            key={item.id}
                                            sx={{
                                                '&:hover': {
                                                    bgcolor: 'action.hover'
                                                }
                                            }}
                                        >
                                            <TableCell>{item.id}</TableCell>
                                            <TableCell>{item.title}</TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={item.status ? "Active" : "Inactive"} 
                                                    color={item.status ? "success" : "default"} 
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>{Utils.getDate(item.created_at)}</TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={(e) => _handleEdit(e, item.id)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={(e) => _handleDelete(e, item.id)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            <DeleteDialog
                url={`/job/schedule/${selectedId}`}
                isOpen={deleteDialogIsOpen}
                key={"mdl" + deleteDialogIsOpen + selectedId}
                setOpen={setDeleteDialogIsOpen}
                notifyDone={_onDeleteDone}
            />

            <CustomModal
                big
                className=""
                key={addDialogIsOpen}
                setOpen={(value) => setAddDialogIsOpen(value)}
                open={addDialogIsOpen}
                title={selectedId ? "Edit Schedule" : "Add New Schedule"}
            >
                <ScheduleForm id={selectedId} onEditDone={_handleFormDone} />
            </CustomModal>
        </Box>
    );
}

export default ScheduleList;

